import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, CornerDownLeft, Mail, Phone, RotateCcw } from "lucide-react";
import { formatEUR } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TromboneClip } from "../art/TromboneClip";
import { DemoFrame, Segmented } from "./DemoFrame";
import { BRAND } from "../brand";

type Storage = "client" | "facture";

const NOTES = [
  { icon: Phone, when: "Appel · 24/09", text: "Règlement annoncé pour le 2 octobre." },
  { icon: Mail, when: "Relance · 18/09", text: "Relance amiable, 3 PDF joints." },
  { icon: CornerDownLeft, when: "Réponse · 19/09", text: "«\u00a0Nous validons le paiement.\u00a0»" },
];

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Pourquoi les notes vivent sur le client : on simule un avoir suivi d'une
 * refacturation. Rangées sur le client, les notes restent accrochées au
 * dossier par le trombone ; rangées sur la facture, elles partent avec elle.
 */
export function MemoryDemo() {
  const [storage, setStorage] = useState<Storage>("client");
  const [relettered, setRelettered] = useState(false);
  const reduce = useReducedMotion();
  const lost = relettered && storage === "facture";

  const changeStorage = (value: Storage) => {
    setStorage(value);
    setRelettered(false);
  };

  return (
    <DemoFrame
      title="Atelier du Rivage · fiche client"
      aside={
        <Segmented
          label="Où sont rangées les notes"
          options={[
            { value: "client", label: "Sur le client" },
            { value: "facture", label: "Sur la facture" },
          ]}
          value={storage}
          onChange={changeStorage}
        />
      }
      footnote={
        storage === "client"
          ? `Données fictives · dans ${BRAND}, notes, appels et réponses sont rangés sur le client`
          : "Données fictives · ce qui arrive quand un outil range les notes sur la facture"
      }
    >
      {/* La facture en cours, remplacée lors du relettrage */}
      <div className="relative h-[68px] overflow-hidden rounded-card border border-border bg-muted">
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={relettered ? "new" : "old"}
            initial={reduce ? { opacity: 0 } : { opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, x: -60 }}
            transition={{ duration: reduce ? 0 : 0.2, ease: EASE }}
            className="absolute inset-0 flex items-center justify-between gap-3 px-4"
          >
            <div>
              <p className="font-mono text-ui font-semibold text-foreground">
                {relettered ? "F-2026-142" : "F-2026-104"}
              </p>
              <p className="text-caption text-muted-foreground">
                {relettered
                  ? "Refacturée après l’avoir AV-2026-031"
                  : "Échue le 11/08/2026"}
              </p>
            </div>
            <span className="text-sm font-semibold text-foreground">
              {formatEUR(1520)}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Les notes, tenues par le trombone */}
      <div className="relative mt-5 min-h-[184px] rounded-card border border-dashed border-border-strong p-4 pt-6">
        <div
          aria-hidden="true"
          className={cn(
            "absolute -top-5 left-5 text-primary transition-opacity duration-300",
            lost && "opacity-30",
          )}
        >
          <TromboneClip height={46} />
        </div>
        <AnimatePresence>
          {!lost &&
            NOTES.map((note) => (
              <motion.div
                key={note.when}
                initial={false}
                exit={{ opacity: 0 }}
                transition={{ duration: reduce ? 0 : 0.15, ease: EASE }}
                className="mb-2 flex gap-3 rounded-md border border-border bg-surface px-3 py-2 last:mb-0"
              >
                <note.icon aria-hidden className="mt-0.5 size-4 shrink-0 text-action" />
                <div className="text-ui">
                  <p className="font-semibold text-foreground">{note.when}</p>
                  <p className="text-muted-foreground">{note.text}</p>
                </div>
              </motion.div>
            ))}
        </AnimatePresence>
        {lost && (
          <p
            className="grid min-h-[140px] place-items-center text-center text-ui text-muted-foreground"
            style={{ animation: "enc-fade-in 180ms var(--ease-quint-out) both" }}
          >
            Historique vide&nbsp;: les notes sont parties avec l’ancienne facture.
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        {relettered ? (
          <button
            type="button"
            onClick={() => setRelettered(false)}
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-border-strong bg-surface px-4 text-ui font-semibold text-foreground transition-colors duration-150 hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <RotateCcw aria-hidden className="size-4" />
            Recommencer
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setRelettered(true)}
            className="inline-flex h-10 cursor-pointer items-center rounded-lg bg-action px-4 text-ui font-semibold text-action-foreground transition-[background-color,scale] duration-150 hover:bg-action-strong active:scale-[0.97] active:duration-[70ms] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring motion-reduce:active:scale-100"
          >
            Simuler un avoir et une refacturation
          </button>
        )}
        {relettered && storage === "client" && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-1 text-caption font-semibold text-success-strong"
            style={{ animation: "enc-fade-in 180ms var(--ease-quint-out) both" }}
          >
            <CheckCircle2 aria-hidden className="size-3.5" />
            Historique conservé
          </span>
        )}
      </div>
    </DemoFrame>
  );
}
